import { type PropsWithChildren, type ReactNode, useRef } from 'react'

import css from './index.module.css'

enum ArrowDirection {
    bottom = 'bottom',
    left = 'left',
    right = 'right',
    top = 'top',
}

type Div = HTMLDivElement

interface Position {
    left: string
    top: string
}

interface Props {
    text: ReactNode
}

export const Tooltip = ({ children, text }: PropsWithChildren<Props>) => {
    const tooltip = useRef<Div>(null)
    const trigger = useRef<Div>(null)

    let timeout: ReturnType<typeof setTimeout>

    const handleMouseEnter = () => {
        const tooltipElement = tooltip.current
        const triggerElement = trigger.current
        if (!tooltipElement || !triggerElement) {
            return
        }

        clearTimeout(timeout)
        tooltipElement.style.display = 'block'
        tooltipElement.style.visibility = 'hidden'
        const [arrowDirection, position] = calcPosition(tooltipElement, triggerElement)
        tooltipElement.className = css.tooltip
        tooltipElement.classList.add(css[arrowDirection])
        tooltipElement.style.top = position.top
        tooltipElement.style.left = position.left
        tooltipElement.style.visibility = 'visible'
    }

    const handleMouseLeave = () => {
        const tooltipElement = tooltip.current
        if (!tooltipElement) {
            return
        }
        timeout = setTimeout(() => tooltipElement.style.display = 'none', 70)
    }

    return (
        <div className={`r-tooltip ${css.container}`}>
            <div
                className={css.tooltip}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                ref={tooltip}>
                {text}
            </div>
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                ref={trigger}>
                {children}
            </div>
        </div>
    )
}

const calcPosition = (tooltip: Div, trigger: Div): [ArrowDirection, Position] => {
    if (window.innerWidth < tooltip.offsetWidth * 1.5) {
        tooltip.style.maxWidth = `${window.innerWidth / 2}px`
    }

    // volá se to až po přidaní maxWidth, protože se to jinak špatně počítá
    const tooltipRect = tooltip.getBoundingClientRect()
    const triggerRect = trigger.getBoundingClientRect()
    // +10 pro šipku
    const height = tooltipRect.height + 10
    const width = tooltipRect.width / 2 - triggerRect.width / 2

    /* --- defaultní hodnoty pro vykreslení tooltipu nad elementem --- */
    let top: string = `-${height}px`

    // ? width se násobí -1 aby se udělal zápor, pokud je tooltip delší než element (ikonka)
    // ? když je ale tooltip kratší než element, tak je tam záporné číslo, které se otočí na kladné
    let left: string = `${width * -1}px`
    let arrowDirection: ArrowDirection = ArrowDirection.bottom

    /* --- pokud se tooltip nad element nevejde, tak se vykreslí pod něj --- */
    if (triggerRect.top - height < 0) {
        // +10 pro šipku
        top = `${triggerRect.height + 10}px`
        left = `${width * -1}px`
        arrowDirection = ArrowDirection.top
    }

    /* --- pokud se tooltip vlevo od elementu nevejde, tak se vykreslí vpravo od něj --- */
    if (triggerRect.left - width < 0) {
        top = `${(height / 2 - triggerRect.height / 2) * -1}px`
        left = `${triggerRect.width + 10}px`
        arrowDirection = ArrowDirection.left
    }

    /* --- pokud se tooltip vpravo od elementu nevejde, tak se vykreslí vlevo od něj --- */
    if (triggerRect.right + width > window.innerWidth) {
        top = `${(height / 2 - triggerRect.height / 2) * -1}px`
        left = `-${tooltipRect.width + 10}px`
        arrowDirection = ArrowDirection.right
    }

    return [
        arrowDirection,
        {
            top,
            left,
        },
    ]
}
